import { ApiProblem } from "@riea-water/api-client";
import type { AuthConfig } from "@riea-water/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router";
import { makeClient } from "./api/client.js";
import { useMe, useModules } from "./api/queries.js";
import { watchInactivity } from "./auth/inactivity.js";
import { useAuth } from "./auth/provider.js";
import { Connections } from "./screens/Connections.js";
import { Finding } from "./screens/Finding.js";
import { Findings } from "./screens/Findings.js";
import { Imports } from "./screens/Imports.js";
import { Overview } from "./screens/Overview.js";
import { Rules } from "./screens/Rules.js";
import { SupplyPoint } from "./screens/SupplyPoint.js";
import { SupplyPoints } from "./screens/SupplyPoints.js";
import { Placeholder } from "./screens/Placeholder.js";
import { FieldJobs } from "./screens/FieldJobs.js";
import { Loading, Mfa, Refused, SignIn } from "./screens/SignIn.js";
import { landingRoute, buildNav } from "./shell/nav.js";
import { Shell } from "./shell/Shell.js";

export function App(props: { config: AuthConfig }) {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/callback" element={<Callback />} />
        <Route path="/*" element={<Console config={props.config} />} />
      </Routes>
    </BrowserRouter>
  );
}

function Callback() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    auth
      .completeSignIn()
      .then(() => navigate("/", { replace: true }))
      .catch((error: unknown) => setFailed(error instanceof Error ? error.message : String(error)));
  }, [auth, navigate]);
  if (failed) return <Loading what={`Sign-in did not complete: ${failed}`} />;
  return <Loading what="Completing sign-in" />;
}

/** Routes with a screen of their own; the rest are placeholders naming their phase. */
const BUILT = [
  "/overview",
  "/imports",
  "/connections",
  "/supply-points",
  "/findings",
  "/rules",
  "/field-jobs",
];

function Console(props: { config: AuthConfig }) {
  const { auth, mock, mockStep, signedIn, ready } = useAuth();
  const queryClient = useQueryClient();
  const [mockNumber] = useState(() => String(10 + Math.floor(Math.random() * 90)));
  const client = useMemo(
    () =>
      makeClient(auth, () => {
        void queryClient.clear();
        void auth.signOut();
      }),
    [auth, queryClient],
  );
  const me = useMe(client, signedIn);
  const modules = useModules(client, signedIn && me.isSuccess);

  // Idle for thirty minutes signs out (ADM-07).
  useEffect(() => {
    if (!signedIn) return;
    return watchInactivity(() => void auth.signOut());
  }, [auth, signedIn]);

  if (!ready) return <Loading what="Checking your session" />;

  if (!signedIn) {
    if (mock && mockStep === "mfa") {
      return (
        <Mfa
          email={`priya.shah@${props.config.tenantName.toLowerCase().replace(/\s+/g, "-")}.example`}
          number={mockNumber}
          demo={props.config.demo}
          onApprove={() => mock.approveMfa()}
          onBack={() => mock.back()}
        />
      );
    }
    return (
      <SignIn
        config={props.config}
        onSignIn={() => void auth.signIn()}
        auto={!mock && props.config.autoSignIn === true}
      />
    );
  }

  if (me.isError) {
    const problem = me.error instanceof ApiProblem ? me.error.problem : null;
    if (problem?.status === 403) {
      return (
        <Refused
          tenantName={props.config.tenantName}
          detail={problem.detail ?? "Your account is not mapped to a role on this workspace."}
          onBack={() => {
            void queryClient.clear();
            void auth.signOut();
          }}
        />
      );
    }
    return <Loading what={`The API did not answer: ${me.error.message}`} />;
  }
  if (!me.data || !modules.data) return <Loading what="Loading your workspace" />;

  const sections = buildNav(modules.data, me.data.role);
  const routes = sections.flatMap((s) => s.items.map((i) => i.route));
  const signOut = () => {
    void queryClient.clear();
    void auth.signOut();
  };
  return (
    <Routes>
      <Route
        element={
          <Shell
            me={me.data}
            modules={modules.data}
            demo={props.config.demo}
            links={props.config.links}
            client={client}
            getToken={() => auth.getToken()}
            onSignOut={signOut}
          />
        }
      >
        <Route index element={<Navigate to={landingRoute(sections)} replace />} />
        {routes.includes("/overview") ? <Route path="/overview" element={<Overview />} /> : null}
        {routes.includes("/imports") ? <Route path="/imports" element={<Imports />} /> : null}
        {routes.includes("/rules") ? <Route path="/rules" element={<Rules />} /> : null}
        {routes.includes("/supply-points") ? (
          <>
            <Route path="/supply-points" element={<SupplyPoints />} />
            <Route path="/supply-points/:spid" element={<SupplyPoint />} />
          </>
        ) : null}
        {routes.includes("/findings") ? (
          <>
            <Route path="/findings" element={<Findings />} />
            <Route path="/findings/:id" element={<Finding />} />
          </>
        ) : null}
        {routes.includes("/field-jobs") ? (
          <Route path="/field-jobs" element={<FieldJobs />} />
        ) : null}
        {routes.includes("/connections") ? (
          <Route path="/connections" element={<Connections />} />
        ) : null}
        {routes
          .filter((r) => !BUILT.includes(r))
          .map((route) => (
            <Route key={route} path={route} element={<Placeholder />} />
          ))}
        <Route path="*" element={<Placeholder />} />
      </Route>
    </Routes>
  );
}
